import {Component, HostListener, OnInit} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { BannerComponent } from '@vg-constellation/angular-19/banner';
import { SpinnerModule } from '@vg-constellation/angular-19/spinner';
import { InternalCalloutComponent } from '@vg-constellation/angular-19/internal-callout';
import { ButtonModule } from '@vg-constellation/angular-19/button';
import { IconModule } from '@vg-constellation/angular-19/icon';
import { StepIndicatorModule } from '@vg-constellation/angular-19/step-indicator';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { setStepIndicatorIndex } from '../../../state/actions/actions';
import { selectRecipient, selectStepIndicatorIndex } from '../../../state/selectors/selector';
import { ConfirmService } from '../../../services/confirm/confirm.service';
import { CaasService } from '../../../services/caas/caas.service';
import { ErrorComponent } from '../../error/error.component';


interface Recipient {
  confirmationId: string;
  interestedParty: boolean;
  confirmationDate: string;
  description: string;
  mediaTypeOnFile: string;
  duplicateMediaType: string;
  name: string;
  address: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  city: string;
  stateCode: string;
  zipCode: string;
  zipCodePlusFour: string;
  foreign: boolean;
  foreignAddressText: string;
  countryCode: string;
  countryName: string;
}

@Component({
  selector: 'app-review-submit',
  templateUrl: './review-submit.component.html',
  styleUrls: ['./review-submit.component.scss'],
  imports: [CommonModule, BannerComponent, SpinnerModule, InternalCalloutComponent, ButtonModule, IconModule, StepIndicatorModule, ErrorComponent],
  standalone: true
})
export class ReviewSubmitComponent implements OnInit {
  isSmallScreen: boolean;
  recipient$: Observable<Recipient>;
  currentStepIndicatorIndex$: Observable<number>;
  errorMessage = '';
  errorWhileSubmit = '';
  loading = false;
  content: any;

  constructor(
    private router: Router,
    private store: Store,
    private confirmService: ConfirmService,
    private caasService: CaasService
  ) {}

  ngOnInit(): void {
    this.checkScreenSize();
    window.scrollTo(0, 0);
    this.content = this.caasService.getPageContent()?.confirmationJourneyContent;
    this.currentStepIndicatorIndex$ = this.store.select(selectStepIndicatorIndex);
    this.recipient$ = this.store.select(selectRecipient);
  }

  getStepText(stepNumber: number): string {
    const template = this.content?.stepProgressTemplate || 'Step ${step-number} of 3';
    return template.replace('${step-number}', String(stepNumber));
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isSmallScreen = window.innerWidth < 576; // Small screen size max width is 576px
  }

  onEdit(){
    this.store.dispatch(setStepIndicatorIndex({ stepIndicatorIndex: 1 }));
  }
  onCancel(): void {
    this.router.navigate(['/']);
  }
  toggleErrorMessage():void {
    this.errorMessage = ''
  }

  onSubmit(): void {
    this.loading = true;
    this.errorMessage = '';
    this.errorWhileSubmit = '';

    this.recipient$.pipe(take(1)).subscribe((recipient) => {
      if (!recipient || !recipient.addressLine1?.trim()) {
        this.errorMessage = 'Recipient address is incomplete. Please go back and select a valid recipient.';
        this.loading = false;
        window.scrollTo({top: 0,behavior: "smooth"})
        return;
      }
      const payload = {
        confirmationId: recipient.confirmationId,
        clientName: recipient.name,
        addressLine1: recipient.addressLine1,
        addressLine2: recipient.addressLine2 || '',
        addressLine3: recipient.addressLine3 || '',
        addressLine4: recipient.addressLine4 || '',
        city: recipient.city,
        stateCode: recipient.stateCode,
        zipCode: recipient.zipCode,
        zipCodePlusFour: recipient.zipCodePlusFour || '',
        foreign: recipient.foreign,
        foreignAddressText: recipient.foreignAddressText,
        countryCode: recipient.countryCode || '',
        countryName: recipient.countryName || ''
      };
      this.confirmService.submitConfirmationReprint(payload).subscribe({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        next: (response) => {
          this.store.dispatch(setStepIndicatorIndex({ stepIndicatorIndex: 3 }));
          this.loading = false;
        },
        error: (error) => {
          this.errorWhileSubmit = 'Submission failed. Please try again later.';
          console.error('Submission failed:', error);
          this.loading = false;
        },
      });
    });
  }
}
